
import { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
    ReferenceLine
} from "recharts";

interface WidgetRendererProps {
    widget: any;
}

const dummyBarData = [
    { name: "Active", value: 12 },
    { name: "Inactive", value: 4 },
    { name: "Pending", value: 7 }
];

const dummyPieData = [
    { name: "Critical", value: 10 },
    { name: "High", value: 20 },
    { name: "Medium", value: 30 }
];

const dummyLineData = [
    { name: "Jan", value: 10 },
    { name: "Feb", value: 20 },
    { name: "Mar", value: 15 },
    { name: "Apr", value: 28 }
];

const COLORS = ["#f97316", "#3b82f6", "#10b981", "#a855f7", "#ef4444"];

const AGG_LABEL_MAP: Record<string, string> = {
    count: "Count",
    sum: "Sum",
    avg: "Average",
    min: "Min",
    max: "Max"
};

/** Format a snake_case or camelCase field name into a readable label */
function formatFieldName(field: string): string {
    if (!field) return "";
    return field
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Safely parse JSON string or return the object as-is */
function safeParse(val: any): any {
    if (!val) return null;
    if (typeof val === "string") {
        try { return JSON.parse(val); } catch { return null; }
    }
    return val;
}

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-gray-800/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-2.5 shadow-2xl text-[11px] text-white flex flex-col gap-1.5 min-w-[180px]">
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex justify-between items-center gap-6">
                        <span className="text-gray-300 font-medium">{formatFieldName(entry.name)}</span>
                        <span className="font-bold text-white font-mono">{entry.value.toLocaleString()}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const CustomActiveDot = (props: any) => {
    const { cx, cy, stroke } = props;
    if (!cx || !cy) return null;
    return (
        <g>
            <circle cx={cx} cy={cy} r={10} fill={stroke} fillOpacity={0.25} />
            <circle cx={cx} cy={cy} r={4.5} fill={stroke} stroke="#ffffff" strokeWidth={1.5} />
        </g>
    );
};

// ─── Gauge SVG Chart ──────────────────────────────────────────────────────
function GaugeChart({ value, label, max: propMax }: { value: number; label?: string; max?: number }) {
    const displayValue = typeof value === "number" ? value : 0;

    const max = propMax || (() => {
        if (displayValue <= 0) return 100;
        const mag = Math.pow(10, Math.floor(Math.log10(Math.max(displayValue, 1))));
        return Math.ceil(displayValue / mag) * mag * 2;
    })();

    const pct = Math.min(Math.max(displayValue / max, 0), 1);
    const W = 240, H = 150;
    const cx = W / 2, cy = 130;
    const R = 90, trackW = 18;

    const toXY = (r: number, p: number): [number, number] => {
        const a = Math.PI * (1 - p);
        return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };

    const arcD = (r: number, p1: number, p2: number, sweep = 1) => {
        const [x1, y1] = toXY(r, p1);
        const [x2, y2] = toXY(r, p2);
        const large = (p2 - p1) > 0.5 ? 1 : 0;
        return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} ${sweep} ${x2.toFixed(2)} ${y2.toFixed(2)}`;
    };

    const zoneColor = (p: number) =>
        p < 0.4 ? "#0d9488" : p < 0.7 ? "#f59e0b" : "#ef4444";

    const ticks = Array.from({ length: 21 }, (_, i) => i / 20);
    const [lx, ly] = toXY(R + trackW / 2 + 12, 0);
    const [rx2, ry2] = toXY(R + trackW / 2 + 12, 1);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
            {/* Background track */}
            <path d={arcD(R, 0, 1)} fill="none" stroke="#1f2937" strokeWidth={trackW} strokeLinecap="round" />
            {/* Zone bands (dimmed) */}
            <path d={arcD(R, 0, 0.4)} fill="none" stroke="#0d9488" strokeWidth={trackW} opacity={0.2} />
            <path d={arcD(R, 0.4, 0.7)} fill="none" stroke="#f59e0b" strokeWidth={trackW} opacity={0.2} />
            <path d={arcD(R, 0.7, 1)} fill="none" stroke="#ef4444" strokeWidth={trackW} opacity={0.2} />
            {/* Value fill */}
            {pct > 0 && <path d={arcD(R, 0, pct)} fill="none" stroke={zoneColor(pct)} strokeWidth={trackW} strokeLinecap="round" />}
            {/* Zone dividers */}
            {[0.4, 0.7].map((p) => {
                const [ox, oy] = toXY(R - trackW / 2 - 1, p);
                const [ix, iy] = toXY(R + trackW / 2 + 1, p);
                return <line key={p} x1={ox} y1={oy} x2={ix} y2={iy} stroke="#0f172a" strokeWidth={2} />;
            })}
            {/* Ticks */}
            {ticks.map((t, i) => {
                const [ox, oy] = toXY(R - trackW / 2 + 2, t);
                const [ix, iy] = toXY(R - trackW / 2 + (i % 5 === 0 ? 9 : 5), t);
                return <line key={i} x1={ox} y1={oy} x2={ix} y2={iy} stroke="#374151" strokeWidth={i % 5 === 0 ? 2 : 1} />;
            })}
            {/* Center text */}
            <text x={cx} y={cy - 14} textAnchor="middle" fill="#6b7280" fontSize={10} fontWeight={500}>
                {label || "Value"}
            </text>
            <text x={cx} y={cy + 12} textAnchor="middle" fill={zoneColor(pct)} fontSize={22} fontWeight={700} fontFamily="monospace">
                {displayValue.toLocaleString()}
            </text>
            {/* Min/Max */}
            <text x={lx} y={ly + 4} textAnchor="middle" fill="#4b5563" fontSize={8}>0</text>
            <text x={rx2} y={ry2 + 4} textAnchor="middle" fill="#4b5563" fontSize={8}>{max.toLocaleString()}</text>
        </svg>
    );
}

function pivotData(data: any) {
    if (!data) return null;
    const columns = data.columns || [];
    const rows = data.rows || [];
    if (columns.length !== 3 || rows.length === 0) return data;

    const valueIsNumeric = rows.some((r: any) => Number.isFinite(Number(r[2])));
    if (!valueIsNumeric) return data;

    const col0Keys = rows.map((r: any) => String(r[0] ?? ""));
    const uniqueDims = new Set(col0Keys);
    if (uniqueDims.size === rows.length) return data;

    const allCategories: string[] = [];
    const catSeen = new Set<string>();
    for (const row of rows) {
        const cat = String(row[1] ?? "");
        if (!catSeen.has(cat)) {
            catSeen.add(cat);
            allCategories.push(cat);
        }
    }

    const categorySet = allCategories.slice(0, 10);

    const dimOrder: any[] = [];
    const dimSeen = new Set<string>();
    for (const row of rows) {
        const key = String(row[0] ?? "");
        if (!dimSeen.has(key)) {
            dimSeen.add(key);
            dimOrder.push(row[0]);
        }
    }

    const lookup: Record<string, Record<string, number>> = {};
    for (const row of rows) {
        const dimKey = String(row[0] ?? "");
        const cat = String(row[1] ?? "");
        if (!lookup[dimKey]) lookup[dimKey] = {};
        lookup[dimKey][cat] = Number(row[2]) || 0;
    }

    const newColumns = [columns[0], ...categorySet];
    const newRows = dimOrder.map(dim => {
        const dimKey = String(dim ?? "");
        return [dim, ...categorySet.map(cat => lookup[dimKey]?.[cat] ?? 0)];
    });

    return {
        columns: newColumns,
        rows: newRows,
        _pivoted: true,
        _categoryLabel: columns[1],
        _metricLabel: columns[2]
    };
}

function GanttRenderer({ widget, xAxisLabel, yAxisLabel }: { widget: any; xAxisLabel?: string; yAxisLabel?: string }) {
    const rawData = widget?.data;
    if (!rawData) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;

    const columns = rawData.columns || [];
    const rows = rawData.rows || [];

    if (columns.length === 0 || rows.length === 0) {
        return <div className="text-gray-500 text-xs p-4">No data available.</div>;
    }

    const { tasks, timelineStart, timelineEnd } = useMemo(() => {
        const list: { name: string; start: number; end: number }[] = [];
        let absoluteMin = Infinity;
        let absoluteMax = -Infinity;

        rows.forEach((row: any) => {
            const name = String(row[0] ?? "Unnamed Task");
            let start = new Date(row[1]).getTime();
            let end = new Date(row[2]).getTime();

            if (isNaN(start)) start = Date.now();
            if (isNaN(end)) end = start + 86400000;

            if (end < start) end = start + 86400000;

            if (start < absoluteMin) absoluteMin = start;
            if (end > absoluteMax) absoluteMax = end;

            list.push({ name, start, end });
        });

        if (absoluteMin === Infinity) absoluteMin = Date.now();
        if (absoluteMax === -Infinity) absoluteMax = Date.now() + 30 * 24 * 60 * 60 * 1000;

        const range = absoluteMax - absoluteMin;
        const padding = range > 0 ? range * 0.05 : 24 * 60 * 60 * 1000;
        const timelineStart = absoluteMin - padding;
        const timelineEnd = absoluteMax + padding;

        return { tasks: list, timelineStart, timelineEnd };
    }, [rows]);

    const numIntervals = 6;
    const timeIntervals = useMemo(() => {
        const arr = [];
        const step = (timelineEnd - timelineStart) / (numIntervals - 1 || 1);
        for (let i = 0; i < numIntervals; i++) {
            const time = timelineStart + i * step;
            arr.push(new Date(time));
        }
        return arr;
    }, [timelineStart, timelineEnd]);

    const formatDate = (date: Date) => {
        return date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric"
        });
    };

    const duration = timelineEnd - timelineStart;

    return (
        <div className="flex flex-col h-full w-full select-none justify-between p-1 bg-gray-950/20 border border-gray-800 rounded-xl overflow-hidden">
            {/* Gantt Header */}
            <div className="flex border-b border-gray-800 bg-gray-900/40 select-none text-[8px] font-bold text-gray-400 py-1 shrink-0">
                <div className="w-[100px] pl-2">Task</div>
                <div className="flex-1 relative flex justify-between px-1">
                    {timeIntervals.map((date, idx) => (
                        <span key={idx} className="-translate-x-1/2 text-center select-none first:translate-x-0 last:translate-x-[-100%]">
                            {formatDate(date)}
                        </span>
                    ))}
                </div>
            </div>

            {/* Gantt Body */}
            <div className="flex-1 overflow-y-auto custom-scroll min-h-0 relative py-0.5 divide-y divide-gray-900/30">
                {tasks.map((task, idx) => {
                    const leftPct = ((task.start - timelineStart) / duration) * 100;
                    const widthPct = ((task.end - task.start) / duration) * 100;

                    return (
                        <div key={idx} className="flex items-center min-h-[26px] group py-0.5">
                            {/* Task Name */}
                            <div className="w-[100px] text-[9px] font-bold text-gray-300 truncate pr-1 pl-2 select-none" title={task.name}>
                                {task.name}
                            </div>

                            {/* Timeline Bar Space */}
                            <div className="flex-1 relative h-5 border-l border-gray-800/40 bg-gray-900/5 flex items-center">
                                {/* Grid lines background overlay */}
                                <div className="absolute inset-0 flex justify-between pointer-events-none">
                                    {timeIntervals.map((_, gIdx) => (
                                        <div key={gIdx} className="h-full border-l border-gray-800/30" />
                                    ))}
                                </div>

                                {/* Gantt Bar */}
                                <div
                                    className="absolute h-3 rounded bg-gradient-to-r from-orange-500 to-orange-600 border border-orange-400/30 flex items-center justify-center text-[7px] font-bold text-white shadow-md cursor-pointer hover:scale-[1.02] transition-all hover:shadow-orange-500/10"
                                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                                    title={`${task.name}: ${new Date(task.start).toLocaleString()} - ${new Date(task.end).toLocaleString()}`}
                                >
                                    {widthPct > 12 && (
                                        <span className="px-1 truncate font-mono">
                                            {((task.end - task.start) / (24 * 60 * 60 * 1000)).toFixed(1)}d
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Gantt Footer */}
            <div className="flex items-center justify-between border-t border-gray-800 pt-1 mt-1 text-[8px] text-gray-505 font-medium px-0.5 shrink-0">
                <div className="flex items-center gap-2 truncate">
                    {xAxisLabel && <span className="truncate">X: {xAxisLabel}</span>}
                    {yAxisLabel && <span className="truncate">Y: {yAxisLabel}</span>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <span className="text-orange-400 font-mono">
                        {new Date(timelineStart).toLocaleDateString()} - {new Date(timelineEnd).toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>
    );
}

function HeatmapRenderer({ widget, xAxisLabel, yAxisLabel }: { widget: any; xAxisLabel?: string; yAxisLabel?: string }) {
    const pivoted = useMemo(() => {
        const rawData = widget?.data;
        if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
            if (Array.isArray(rawData.columns) && Array.isArray(rawData.rows)) {
                return pivotData(rawData);
            }
        }
        return null;
    }, [widget]);

    if (!pivoted) return <div className="text-gray-500 text-xs p-4">No data returned.</div>;

    const columns = pivoted.columns || [];
    const rows = pivoted.rows || [];

    if (columns.length === 0 || rows.length === 0) {
        return <div className="text-gray-500 text-xs p-4">No data available.</div>;
    }

    const hasSplit = columns.length > 2;
    const yCategories = hasSplit ? columns.slice(1) : [columns[1] || "Value"];
    const xCategories = rows.map((r: any) => String(r[0] ?? ""));

    const maxVal = (() => {
        let max = 0;
        rows.forEach((row: any) => {
            const values = hasSplit ? row.slice(1) : [row[1]];
            values.forEach((v: any) => {
                const val = Number(v) || 0;
                if (val > max) max = val;
            });
        });
        return max || 1;
    })();

    return (
        <div className="flex flex-col h-full w-full justify-between p-1">
            <div className="relative flex-1 flex min-h-0 min-w-0 gap-3 mt-1">
                {/* Y-Axis labels (Bucket Values) */}
                <div className="flex flex-col justify-around text-right pr-1 select-none min-w-[50px] max-w-[90px] truncate text-[9px] font-semibold text-gray-400">
                    {xCategories.map((xCat: any) => (
                        <div key={xCat} className="truncate py-0.5" title={String(xCat)}>
                            {String(xCat)}
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div className="flex-1 flex flex-col min-w-0">
                    <div className="flex-1 flex flex-col justify-around gap-1 min-h-0">
                        {xCategories.map((xCat: any, xIdx: number) => (
                            <div key={xCat} className="flex-1 flex gap-1 min-h-0">
                                {yCategories.map((yCat: any, yIdx: number) => {
                                    const rawVal = hasSplit ? rows[xIdx][yIdx + 1] : rows[xIdx][1];
                                    const value = Number(rawVal) || 0;
                                    const pct = maxVal > 0 ? value / maxVal : 0;

                                    const cellStyle = {
                                        backgroundColor: value > 0
                                            ? `rgba(249, 115, 22, ${0.12 + pct * 0.88})`
                                            : "rgba(31, 41, 55, 0.25)",
                                        border: value > 0
                                            ? `1px solid rgba(249, 115, 22, ${0.2 + pct * 0.6})`
                                            : "1px solid rgba(75, 85, 99, 0.2)"
                                    };

                                    return (
                                        <div
                                            key={String(yCat)}
                                            className="flex-1 rounded cursor-pointer transition-all duration-150 hover:scale-[1.03] relative flex items-center justify-center group"
                                            style={cellStyle}
                                            title={`${xCat} / ${yCat}: ${value}`}
                                        >
                                            {value > 0 && (
                                                <span className={`text-[9px] font-bold select-none ${pct > 0.5 ? "text-white" : "text-orange-400"}`}>
                                                    {value}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* X-Axis labels (Split Series) */}
                    <div className="flex gap-1 pt-1.5 select-none h-4 items-center">
                        {yCategories.map((yCat: any) => (
                            <div key={yCat} className="flex-1 text-center text-[9px] font-semibold text-gray-400 truncate px-0.5" title={String(yCat)}>
                                {String(yCat)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Legend / Labels footer */}
            <div className="flex items-center justify-between border-t border-gray-800/40 pt-1 mt-1 text-[8px] text-gray-500 font-medium px-0.5 shrink-0">
                <div className="flex items-center gap-2 truncate">
                    {xAxisLabel && <span className="truncate">X: {xAxisLabel}</span>}
                    {yAxisLabel && <span className="truncate">Y: {yAxisLabel}</span>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <span>Density</span>
                    <div className="h-1.5 w-8 rounded-sm bg-gradient-to-r from-orange-500/20 to-orange-500 border border-orange-500/30" />
                    <span className="font-mono text-orange-400 font-semibold">{maxVal}</span>
                </div>
            </div>
        </div>
    );
}

// ─── Candlestick (Trend Analysis) SVG Chart ──────────────────────────────────
interface CandleData {
    label: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume?: number;
}

function CandlestickRenderer({ widget, xAxisLabel, yAxisLabel }: { widget: any; xAxisLabel?: string; yAxisLabel?: string }) {
    const candles = useMemo<CandleData[]>(() => {
        const rawData = widget?.data;

        // Format 1: columns/rows with [label, open, high, low, close, ?volume]
        if (rawData?.columns && Array.isArray(rawData.rows) && rawData.rows.length > 0) {
            const cols: string[] = (rawData.columns as string[]).map((c: string) => (c || "").toLowerCase());
            // Try to find OHLC columns by name
            const oIdx = cols.findIndex((c: string) => c.includes("open"));
            const hIdx = cols.findIndex((c: string) => c.includes("high"));
            const lIdx = cols.findIndex((c: string) => c.includes("low"));
            const cIdx = cols.findIndex((c: string) => c.includes("close"));
            const vIdx = cols.findIndex((c: string) => c.includes("vol"));

            if (oIdx >= 0 && hIdx >= 0 && lIdx >= 0 && cIdx >= 0) {
                return rawData.rows.map((row: any) => ({
                    label: String(row[0] ?? ""),
                    open: Number(row[oIdx]) || 0,
                    high: Number(row[hIdx]) || 0,
                    low: Number(row[lIdx]) || 0,
                    close: Number(row[cIdx]) || 0,
                    volume: vIdx >= 0 ? Number(row[vIdx]) || 0 : undefined,
                }));
            }

            // Fallback: assume columns are [label, open, high, low, close, ?volume]
            if (rawData.rows[0]?.length >= 5) {
                return rawData.rows.map((row: any) => ({
                    label: String(row[0] ?? ""),
                    open: Number(row[1]) || 0,
                    high: Number(row[2]) || 0,
                    low: Number(row[3]) || 0,
                    close: Number(row[4]) || 0,
                    volume: row[5] != null ? Number(row[5]) || 0 : undefined,
                }));
            }
        }

        // Format 2: Array of objects [{label, open, high, low, close, volume?}]
        if (Array.isArray(rawData) && rawData.length > 0 && rawData[0]?.open !== undefined) {
            return rawData.map((item: any) => ({
                label: String(item.label || item.name || item.date || item.key || ""),
                open: Number(item.open) || 0,
                high: Number(item.high) || 0,
                low: Number(item.low) || 0,
                close: Number(item.close) || 0,
                volume: item.volume != null ? Number(item.volume) || 0 : undefined,
            }));
        }

        // Dummy data for preview
        return [
            { label: "Mon", open: 44, high: 52, low: 40, close: 50 },
            { label: "Tue", open: 50, high: 58, low: 46, close: 48 },
            { label: "Wed", open: 48, high: 55, low: 42, close: 53 },
            { label: "Thu", open: 53, high: 60, low: 50, close: 56 },
            { label: "Fri", open: 56, high: 62, low: 52, close: 54 },
            { label: "Sat", open: 54, high: 58, low: 48, close: 57 },
            { label: "Sun", open: 57, high: 65, low: 55, close: 63 },
        ];
    }, [widget]);

    if (candles.length === 0) {
        return <div className="text-gray-500 text-xs p-4 flex items-center justify-center h-full">No candlestick data available.</div>;
    }

    // Use a large viewBox so text renders at readable sizes
    const hasVolume = candles.some(c => c.volume !== undefined && c.volume > 0);
    const maxVolume = hasVolume ? Math.max(...candles.map(c => c.volume || 0), 1) : 1;
    const W = 800, H = 400;
    const MG = { top: 20, right: 60, bottom: hasVolume ? 110 : 80, left: 50 };
    const chartW = W - MG.left - MG.right;
    const chartH = H - MG.top - MG.bottom;
    const VOL_H = hasVolume ? 30 : 0;

    // Calculate ranges
    const allHighs = candles.map(c => c.high);
    const allLows = candles.map(c => c.low);
    const dataMin = Math.min(...allLows);
    const dataMax = Math.max(...allHighs);
    const range = dataMax - dataMin || 1;
    const pad = range * 0.08;
    const yMin = dataMin - pad;
    const yRange = (dataMax + pad) - yMin;

    // Y-axis ticks (5 ticks)
    const yTicks = useMemo(() => {
        const ticks: number[] = [];
        const step = yRange / 4;
        for (let i = 0; i <= 4; i++) {
            ticks.push(yMin + step * i);
        }
        return ticks;
    }, [yMin, yRange]);

    // Label sampling — show at most ~12 labels
    const maxLabels = 12;
    const labelStep = Math.max(1, Math.ceil(candles.length / maxLabels));

    const count = candles.length;
    const gap = chartW / count;
    const candleWidth = Math.min(gap * 0.6, 20);

    const toY = (val: number) => MG.top + (1 - (val - yMin) / yRange) * chartH;

    return (
        <div className="h-full w-full min-h-[200px] p-1">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full">
                {/* Chart background */}
                <rect x={MG.left} y={MG.top} width={chartW} height={chartH} fill="#0f172a" rx="4" />

                {/* Y-Axis grid lines + labels */}
                {yTicks.map((tick, i) => {
                    const y = toY(tick);
                    return (
                        <g key={`ytick-${i}`}>
                            <line x1={MG.left} y1={y} x2={MG.left + chartW} y2={y} stroke="#1e293b" strokeWidth="1" strokeDasharray="4 4" />
                            <text x={MG.left - 8} y={y + 4} fill="#6b7280" fontSize="11" fontFamily="monospace" textAnchor="end">
                                {tick.toFixed(tick >= 1000 ? 0 : 1)}
                            </text>
                        </g>
                    );
                })}

                {/* Candles */}
                {candles.map((candle, idx) => {
                    const cx = MG.left + gap * idx + gap / 2;
                    const isBullish = candle.close >= candle.open;
                    const bodyColor = isBullish ? "#22c55e" : "#ef4444";
                    const wickColor = isBullish ? "#16a34a" : "#dc2626";

                    const yHigh = toY(candle.high);
                    const yLow = toY(candle.low);
                    const yOpen = toY(candle.open);
                    const yClose = toY(candle.close);
                    const bodyTop = Math.min(yOpen, yClose);
                    const bodyHeight = Math.max(Math.abs(yOpen - yClose), 2);

                    return (
                        <g key={`candle-${idx}`}>
                            {/* Wick */}
                            <line x1={cx} y1={yHigh} x2={cx} y2={yLow} stroke={wickColor} strokeWidth="1.5" />
                            {/* Candle body */}
                            <rect x={cx - candleWidth / 2} y={bodyTop} width={candleWidth} height={bodyHeight} fill={bodyColor} rx="1" stroke={wickColor} strokeWidth="0.5" />
                            {/* Hover area */}
                            <rect x={cx - gap / 2} y={MG.top} width={gap} height={chartH} fill="transparent">
                                <title>{`${candle.label}\nO: ${candle.open}  H: ${candle.high}\nL: ${candle.low}  C: ${candle.close}${candle.volume !== undefined ? `\nVol: ${candle.volume.toLocaleString()}` : ""}`}</title>
                            </rect>
                            {/* X-axis label (sampled + rotated) */}
                            {idx % labelStep === 0 && (
                                <text
                                    x={cx} y={MG.top + chartH + 14}
                                    fill="#9ca3af" fontSize="10" textAnchor="end" fontFamily="sans-serif"
                                    transform={`rotate(-45, ${cx}, ${MG.top + chartH + 14})`}
                                >
                                    {candle.label.length > 12 ? candle.label.slice(0, 12) + "…" : candle.label}
                                </text>
                            )}
                            {/* Volume bar */}
                            {hasVolume && candle.volume !== undefined && candle.volume > 0 && (
                                <rect
                                    x={cx - candleWidth / 2}
                                    y={H - MG.bottom + (MG.bottom - VOL_H - 10) + VOL_H - (candle.volume / maxVolume) * VOL_H}
                                    width={candleWidth}
                                    height={(candle.volume / maxVolume) * VOL_H}
                                    fill={isBullish ? "rgba(34, 197, 94, 0.25)" : "rgba(239, 68, 68, 0.25)"}
                                    rx="1"
                                />
                            )}
                        </g>
                    );
                })}

                {/* Chart border */}
                <line x1={MG.left} y1={MG.top} x2={MG.left} y2={MG.top + chartH} stroke="#374151" strokeWidth="1" />
                <line x1={MG.left} y1={MG.top + chartH} x2={MG.left + chartW} y2={MG.top + chartH} stroke="#374151" strokeWidth="1" />
            </svg>

            {/* Footer legend */}
            <div className="flex items-center justify-between px-1 mt-0.5">
                {/* <div className="flex items-center gap-3 text-[9px] text-gray-400">
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-sm bg-green-500" />
                        <span>Bullish</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-sm bg-red-500" />
                        <span>Bearish</span>
                    </div>
                    {hasVolume && (
                        <span className="text-gray-500 ml-1">Vol bars shown</span>
                    )}
                </div> */}
                <div className="flex items-center gap-2 text-[8px] text-gray-500">
                    {xAxisLabel && <span>X: {xAxisLabel}</span>}
                    {yAxisLabel && <span>Y: {yAxisLabel}</span>}
                </div>
            </div>
        </div>
    );
}

export default function WidgetRenderer({ widget }: WidgetRendererProps) {
    const rawType = (widget?.widget_type || widget?.chart_type || "").toLowerCase();

    // Normalize widget type — horizontal_bar must be checked before "bar"
    const widgetType =
        rawType === "horizontal_bar" || rawType.includes("horizontal") ? "horizontal_bar"
            : rawType === "gauge" || rawType.includes("gauge") ? "gauge"
                : rawType.includes("heat") ? "heatmap"
                    : rawType.includes("gantt") ? "gantt"
                        : rawType.includes("candle") || rawType.includes("candlestick") || rawType.includes("trend") ? "candlestick"
                            : rawType.includes("bar") ? "bar"
                                : rawType.includes("pie") ? "pie"
                                    : rawType.includes("donut") ? "donut"
                                        : rawType.includes("line") ? "line"
                                            : rawType.includes("area") ? "area"
                                                : rawType.includes("table") ? "table"
                                                    : rawType.includes("stat") || rawType.includes("metric") ? "stat"
                                                        : rawType;

    // ─── Derive axis labels ──────────────────────────────────────────────────
    const { xAxisLabel, yAxisLabel } = useMemo(() => {
        const queryDef = safeParse(widget?.query_definition);
        const visConfig = safeParse(widget?.visualization_config) || {};

        // From query_definition → axis
        const xField: string = queryDef?.axis?.x_axis?.field || "";
        const yAggType: string = queryDef?.axis?.y_axis?.[0]?.type || "";
        const yAggLabel: string = queryDef?.axis?.y_axis?.[0]?.label || "";

        // Priority: visualization_config custom label → query-derived label → empty
        const xLabel =
            visConfig.x_axis_label ||
            formatFieldName(xField) ||
            "";

        const yLabel =
            visConfig.y_axis_label ||
            yAggLabel ||
            AGG_LABEL_MAP[yAggType] ||
            (yAggType ? formatFieldName(yAggType) : "");

        return { xAxisLabel: formatFieldName(xLabel), yAxisLabel: formatFieldName(yLabel) };
    }, [widget]);
    const visConfig = safeParse(widget?.visualization_config ?? widget?.visualization_config)
    const colorPalette: string[] = visConfig?.color_palette?.length
        ? visConfig.color_palette
        : ['#f97316', '#3b82f6', '#10b981', '#a855f7', '#ef4444']
    const threshold = visConfig?.threshold;


    // ─── Build chart data ────────────────────────────────────────────────────
    // ─── Build chart data ────────────────────────────────────────────────────
    const chartData = useMemo(() => {
        const rawData = widget?.data;

        // 1. Array of key/value objects
        if (Array.isArray(rawData)) {
            return rawData.map((item: any) => ({
                name: formatFieldName(String(item.key ?? "")),
                value: item.value
            }));
        }

        // 2. Objects with labels and series (legacy format)
        if (rawData?.labels && rawData?.series) {
            const seriesData = rawData.series[0]?.data || [];
            return rawData.labels.map((label: string, i: number) => ({
                name: label,
                value: seriesData[i] ?? 0
            }));
        }

        // 3. New Columns and Rows format
        if (rawData && typeof rawData === "object") {
            if (Array.isArray(rawData.columns) && Array.isArray(rawData.rows)) {
                const cols = rawData.columns;
                const rws = rawData.rows;
                if (rws.length > 0) {
                    // Find a numeric column to use as value
                    let valIdx = 1;
                    for (let i = 1; i < cols.length; i++) {
                        if (rws.some((r: any) => typeof r[i] === "number")) {
                            valIdx = i;
                            break;
                        }
                    }
                    return rws.map((row: any) => ({
                        name: formatFieldName(String(row[0] ?? "")),
                        value: row[valIdx]
                    }));
                }
            }
        }

        // Fallback dummy data
        if (widgetType === "pie" || widgetType === "donut") return dummyPieData;
        if (widgetType === "line" || widgetType === "area") return dummyLineData;
        return dummyBarData;
    }, [widget, widgetType]);

    // ─── Build columns & rows (primarily for dynamic tables) ──────────────────
    const { columns, rows } = useMemo(() => {
        const rawData = widget?.data;

        // 1. If it has columns and rows directly
        if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
            if (Array.isArray(rawData.columns) && Array.isArray(rawData.rows)) {
                return {
                    columns: rawData.columns.map(formatFieldName),
                    rows: rawData.rows
                };
            }
            if (rawData.labels && rawData.series) {
                const seriesName = rawData.series[0]?.name || yAxisLabel || "Value";
                const seriesData = rawData.series[0]?.data || [];
                return {
                    columns: [xAxisLabel || "Name", seriesName],
                    rows: rawData.labels.map((label: string, i: number) => [
                        label,
                        seriesData[i] ?? 0
                    ])
                };
            }
        }

        // 2. If it is an array of items
        if (Array.isArray(rawData) && rawData.length > 0) {
            const first = rawData[0];
            if (first && typeof first === "object") {
                if ("key" in first || "value" in first) {
                    return {
                        columns: [xAxisLabel || "Name", yAxisLabel || "Value"],
                        rows: rawData.map((item: any) => [
                            formatFieldName(String(item.key ?? "")),
                            item.value
                        ])
                    };
                }

                const keys = Object.keys(first);
                return {
                    columns: keys.map(formatFieldName),
                    rows: rawData.map((item: any) => keys.map((k) => item[k]))
                };
            }
        }

        // 3. Fallback to dummy/chartData
        return {
            columns: [xAxisLabel || "Name", yAxisLabel || "Value"],
            rows: chartData.map((d: any) => [d.name, d.value])
        };
    }, [widget, xAxisLabel, yAxisLabel, chartData]);

    // ─── Shared axis label prop builder ─────────────────────────────────────
    const makeAxisLabel = (text: string, angle: number, position: any, offset: number) =>
        text ? { value: text, angle, position, offset, fill: "#6b7280", fontSize: 10, fontWeight: 600 } as any : undefined;

    const renderContent = () => {
        switch (widgetType) {

            // ── Heatmap ──────────────────────────────────────────────────────
            case "heatmap":
                return (
                    <HeatmapRenderer widget={widget} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
                );

            // ── Gantt ────────────────────────────────────────────────────────
            case "gantt":
                return (
                    <GanttRenderer widget={widget} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
                );

            // ── Candlestick (Trend Analysis) ─────────────────────────────────
            case "candlestick":
                return (
                    <CandlestickRenderer widget={widget} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} />
                );

            // ── Vertical Bar ────────────────────────────────────────────────
            case "bar":
                return (
                    <div className="h-full min-h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{
                                    top: 8,
                                    right: 8,
                                    left: yAxisLabel ? 10 : -8,
                                    bottom: xAxisLabel ? 28 : 4
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    tick={{ fill: "#9ca3af" }}
                                    label={makeAxisLabel(xAxisLabel, 0, "insideBottom", -12)}
                                />
                                <YAxis
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    width={yAxisLabel ? 52 : 36}
                                    label={makeAxisLabel(yAxisLabel, -90, "insideLeft", 12)}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={false} />
                                <Bar dataKey="value" fill={colorPalette[0]} radius={[4, 4, 0, 0]} />
                                {threshold?.show && (
                                    <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                );

            // ── Horizontal Bar ──────────────────────────────────────────────
            case "horizontal_bar":
                return (
                    <div className="h-full min-h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                layout="vertical"
                                margin={{
                                    top: 8,
                                    right: 8,
                                    left: 80,
                                    bottom: yAxisLabel ? 28 : 4
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                                {/* For horizontal bar: numeric values on X, category field on Y */}
                                <XAxis
                                    type="number"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    label={makeAxisLabel(yAxisLabel, 0, "insideBottom", -12)}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    width={80}
                                    label={makeAxisLabel(xAxisLabel, -90, "insideLeft", 12)}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={false} />
                                <Bar dataKey="value" fill={colorPalette[0]} radius={[0, 4, 4, 0]} />
                                {threshold?.show && (
                                    <ReferenceLine x={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                );

            // ── Pie / Donut ─────────────────────────────────────────────────
            case "pie":
            case "donut": {
                const total = chartData.reduce((s: number, d: any) => s + (Number(d.value) || 0), 0);
                return (
                    <div className="h-full min-h-[140px] grid grid-cols-[minmax(0,1fr)_170px] gap-1">
                        <div className="min-w-0 h-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="52%"
                                        cy="50%"
                                        outerRadius="88%"
                                        innerRadius="60%"
                                        label={false}
                                        labelLine={false}
                                        stroke="none"
                                    >
                                        {chartData.map((_: any, i: number) => (
                                            <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="max-h-[140px] self-center rounded-lg border border-gray-700/50 bg-gray-900/40 overflow-y-auto custom-scroll">
                            <div className="p-1.5 space-y-1">
                                {chartData.map((entry: any, i: number) => {
                                    const pct = total > 0 ? `${((Number(entry.value) / total) * 100).toFixed(0)}%` : "0%";
                                    return (
                                        <div key={`${entry.name}-${i}`} className="flex items-center gap-1 px-1 py-1 rounded bg-gray-800/20">
                                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                            <div className="min-w-0 flex-1 text-[10px] text-gray-200 truncate">
                                                <span className="truncate">{entry.name}</span>
                                                <span className="text-gray-400 ml-1">{pct}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            }

            // ── Line ────────────────────────────────────────────────────────
            case "line":
                return (
                    <div className="h-full min-h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 8,
                                    right: 8,
                                    left: yAxisLabel ? 10 : -8,
                                    bottom: xAxisLabel ? 28 : 4
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    label={makeAxisLabel(xAxisLabel, 0, "insideBottom", -12)}
                                />
                                <YAxis
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    width={yAxisLabel ? 52 : 36}
                                    label={makeAxisLabel(yAxisLabel, -90, "insideLeft", 12)}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Line type="monotone" dataKey="value" stroke={colorPalette[0]} strokeWidth={2} dot={{ r: 3 }} activeDot={<CustomActiveDot />} />
                                {threshold?.show && (
                                    <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                );

            // ── Area ────────────────────────────────────────────────────────
            case "area":
                return (
                    <div className="h-full min-h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={chartData}
                                margin={{
                                    top: 8,
                                    right: 8,
                                    left: yAxisLabel ? 10 : -8,
                                    bottom: xAxisLabel ? 28 : 4
                                }}
                            >
                                <defs>
                                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={colorPalette[0]} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={colorPalette[0]} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                                <XAxis
                                    dataKey="name"
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    label={makeAxisLabel(xAxisLabel, 0, "insideBottom", -12)}
                                />
                                <YAxis
                                    stroke="#9ca3af"
                                    fontSize={10}
                                    width={yAxisLabel ? 52 : 36}
                                    label={makeAxisLabel(yAxisLabel, -90, "insideLeft", 12)}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="value" stroke={colorPalette[0]} fillOpacity={1} fill="url(#colorValue)" strokeWidth={2} dot={{ r: 3 }} activeDot={<CustomActiveDot />} />
                                {threshold?.show && (
                                    <ReferenceLine y={threshold.value} stroke={threshold.color || "#E7664C"} strokeDasharray="3 3" />
                                )}
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                );

            // ── Gauge ─────────────────────────────────────────────────────────
            case "gauge": {
                // Gauge value: try columns/rows, then array format, then first chartData value
                const rawData = widget?.data;
                let gaugeVal = 0;
                if (rawData?.columns && rawData?.rows?.[0]) {
                    const lastIdx = rawData.columns.length - 1;
                    gaugeVal = Number(rawData.rows[0][lastIdx] ?? rawData.rows[0][0] ?? 0);
                } else if (Array.isArray(rawData) && rawData[0]) {
                    gaugeVal = Number(rawData[0]?.value ?? 0);
                } else {
                    gaugeVal = Number(chartData[0]?.value ?? 0);
                }
                return (
                    <div className="h-full min-h-[140px] flex items-center justify-center p-2">
                        <GaugeChart value={gaugeVal} label={yAxisLabel || widget?.name || "Value"} />
                    </div>
                );
            }

            // ── Stat / Metric ────────────────────────────────────────────────
            case "stat":
                return (
                    <div className="h-full flex flex-col items-center justify-center">
                        <div
                            className="text-4xl font-bold"
                            style={{ color: colorPalette[0] }}
                        >
                            {typeof chartData[0]?.value === "number"
                                ? chartData[0].value.toLocaleString()
                                : (chartData[0]?.value ?? 0)}
                        </div>
                        <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">
                            {yAxisLabel || widget?.name || ""}
                        </div>
                    </div>
                );

            // ── Table ────────────────────────────────────────────────────────
            case "table":
                return (
                    <div className="h-full overflow-auto border border-gray-700/50 rounded-lg scrollbar-hide">
                        <table className="w-full text-left text-[11px] border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 text-gray-400 uppercase border-b border-gray-800">
                                <tr>
                                    {columns.map((col: string, idx: number) => (
                                        <th key={idx} className={`px-3 py-2 font-semibold ${idx > 0 ? "text-right" : ""}`}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800 bg-gray-950/20">
                                {rows.map((row: any[], rIdx: number) => (
                                    <tr key={rIdx} className="hover:bg-gray-800/30 transition-colors">
                                        {row.map((val: any, cIdx: number) => (
                                            <td
                                                key={cIdx}
                                                className={`px-3 py-2 text-gray-300 truncate ${cIdx > 0 ? "text-right font-mono text-gray-400" : ""
                                                    }`}
                                            >
                                                {typeof val === "number" ? val.toLocaleString() : String(val ?? "")}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );

            default:
                return (
                    <div className="h-full flex items-center justify-center text-xs text-gray-500 border border-dashed border-gray-800 rounded-lg">
                        No data available for {widget?.widget_type || widget?.chart_type}
                    </div>
                );
        }
    };

    return <div className="w-full h-full">{renderContent()}</div>;
}